/**
 * MarketAssignmentPage — Iter-411l.
 *
 * Admin-only page for the Talent Market Backfill flow. Sections:
 *
 *   1. Coverage stats (total / assigned / unassigned + % by method)
 *   2. Batch actions
 *        • Run rules pass (fast, safe, deterministic SQL)
 *        • Run full hybrid pass (rules + AI, queues low-confidence)
 *   3. Review queue (AI suggestions awaiting admin approval)
 *
 * No new nav item — the caller wires this into an existing admin route.
 * Route: `/admin/market-assignment`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  MapPin, Sparkles, Loader2, CheckCircle2, XCircle,
  RefreshCcw, Zap, ClipboardList, Globe, ChevronRight, Plane,
} from 'lucide-react';
import api from '../../utils/api';

const METHOD_LABEL = {
  rule: 'Rule (city+state match)',
  ai:   'AI (auto-applied ≥90%)',
  manual: 'Admin manual',
  queue_approved: 'Queue approved',
};

export default function MarketAssignmentPage() {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [dryResult, setDryResult] = useState(null);
  const [rollbackSince, setRollbackSince] = useState('');
  const [rollbackPreview, setRollbackPreview] = useState(null);
  const [geo, setGeo] = useState(null);
  const [openCountry, setOpenCountry] = useState('CA');

  const load = useCallback(async () => {
    try {
      const [statsRes, queueRes, geoRes] = await Promise.all([
        api.get('/admin/market-assignment/stats'),
        api.get('/admin/market-assignment/queue'),
        api.get('/admin/market-assignment/geo-overview'),
      ]);
      setStats(statsRes.data);
      setQueue(queueRes.data.queue || []);
      setGeo(geoRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load market stats');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runRulesFast = async () => {
    setBusy(true);
    try {
      const res = await api.post('/admin/market-assignment/run-rules-fast');
      toast.success(
        `Rule pass: ${res.data.applied} talent assigned · Coverage ${res.data.coverage_before?.pct}% → ${res.data.coverage_after?.pct}%`,
        { duration: 8000 },
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rule pass failed');
    } finally { setBusy(false); }
  };

  const runFullBatch = async () => {
    setBusy(true);
    toast.info('Running full rule + AI backfill — this may take a few minutes for large batches.');
    try {
      const res = await api.post('/admin/market-assignment/run-batch', {
        limit: null, dry_run: false, use_ai: true,
      });
      toast.success(
        `Rules: ${res.data.rule_applied} · AI auto-applied: ${res.data.ai_applied} · Queued for review: ${res.data.queued} · Still unassigned: ${res.data.unassigned_after}`,
        { duration: 12000 },
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Batch failed');
    } finally { setBusy(false); }
  };

  const runDryRun = async () => {
    setBusy(true);
    setDryResult(null);
    try {
      const res = await api.post('/admin/market-assignment/run-batch', {
        limit: null, dry_run: true, use_ai: false,
      });
      setDryResult(res.data);
      toast.success(
        `Dry-run audit: ${res.data.rule_applied} would be assigned by rules · ${res.data.unassigned_after} would remain unassigned (nothing changed)`,
        { duration: 10000 },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dry-run failed');
    } finally { setBusy(false); }
  };

  const previewRollback = async () => {
    if (!rollbackSince) { toast.error('Pick a cutoff date/time first'); return; }
    setBusy(true);
    setRollbackPreview(null);
    try {
      const iso = new Date(rollbackSince).toISOString();
      const res = await api.post('/admin/market-assignment/rollback', { since: iso, dry_run: true });
      setRollbackPreview(res.data);
      toast.info(`${res.data.would_rollback} assignment(s) would be rolled back`, { duration: 8000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rollback preview failed');
    } finally { setBusy(false); }
  };

  const applyRollback = async () => {
    if (!rollbackSince) { toast.error('Pick a cutoff date/time first'); return; }
    const n = rollbackPreview?.would_rollback;
    if (!window.confirm(`Roll back ${n != null ? n : 'all'} assignment(s) made on/after ${rollbackSince}?\n\nEach affected talent's PREVIOUS primary market is restored. Manually-chosen markets are preserved.`)) return;
    setBusy(true);
    try {
      const iso = new Date(rollbackSince).toISOString();
      const res = await api.post('/admin/market-assignment/rollback', { since: iso, dry_run: false });
      toast.success(`Rolled back ${res.data.rolled_back} assignment(s)`, { duration: 9000 });
      setRollbackPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rollback failed');
    } finally { setBusy(false); }
  };

  const reassignTalent = async (talentId) => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/market-assignment/assign/${talentId}`);
      toast.success(`Re-assigned: ${res.data?.market_slug || res.data?.method || 'done'}`, { duration: 6000 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-assign failed');
    } finally { setBusy(false); }
  };

  const approveQueue = async (queueId) => {
    setLoadingQueue(true);
    try {
      await api.post(`/admin/market-assignment/queue/${queueId}/approve`);
      toast.success('Assignment approved');
      setQueue((q) => q.filter((x) => x.id !== queueId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally { setLoadingQueue(false); }
  };

  const rejectQueue = async (queueId) => {
    const reason = window.prompt('Reason for rejection (optional):') || null;
    setLoadingQueue(true);
    try {
      await api.post(`/admin/market-assignment/queue/${queueId}/reject`, { reason });
      toast.success('Assignment rejected');
      setQueue((q) => q.filter((x) => x.id !== queueId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally { setLoadingQueue(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="market-assignment-page">
      <div className="flex items-center gap-2 mb-4">
        <MapPin className="w-5 h-5 text-cyan-600" />
        <h1 className="text-2xl font-bold text-slate-900">Talent Market Assignment</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Backfill <code>primary_market_id</code> for every talent using rules first,
        then AI for ambiguous records. Low-confidence AI suggestions land in the
        review queue below for admin approval — nothing auto-applies below 90% confidence.
      </p>

      {/* Coverage stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="market-stats-grid">
        <StatTile label="Total talent" value={stats?.total_talent ?? '—'} />
        <StatTile label="Assigned" value={stats?.assigned ?? '—'} tone="emerald" />
        <StatTile label="Unassigned" value={stats?.unassigned ?? '—'} tone="amber" />
        <StatTile
          label="Coverage"
          value={stats ? `${stats.coverage_pct}%` : '—'}
          tone={stats && stats.coverage_pct >= 90 ? 'emerald' : stats && stats.coverage_pct >= 60 ? 'amber' : 'rose'}
        />
      </div>

      {/* Method breakdown */}
      {stats?.by_method && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="text-[11px] uppercase text-slate-500 mb-2 font-semibold">By assignment method</div>
          <div className="flex flex-wrap gap-3 text-sm">
            {Object.entries(stats.by_method).map(([m, n]) => (
              <span key={m} className="text-slate-700">
                <span className="font-semibold" data-testid={`method-count-${m}`}>{n}</span>
                <span className="text-slate-500 ml-1">{METHOD_LABEL[m] || m}</span>
              </span>
            ))}
            {stats.queue_pending > 0 && (
              <span className="text-amber-700 font-semibold" data-testid="queue-pending-badge">
                {stats.queue_pending} awaiting review →
              </span>
            )}
          </div>
        </div>
      )}

      {/* Batch actions */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={runDryRun}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-60"
          data-testid="run-dry-run-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />}
          Dry-run audit (no changes)
        </button>
        <button
          type="button"
          onClick={runRulesFast}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-60"
          data-testid="run-rules-fast-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          Run rules (fast, safe)
        </button>
        <button
          type="button"
          onClick={runFullBatch}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-60"
          data-testid="run-full-batch-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Run full hybrid (rules + AI)
        </button>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 px-3 py-2 text-sm text-slate-700 disabled:opacity-60"
          data-testid="refresh-stats-btn"
        >
          <RefreshCcw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Dry-run audit result */}
      {dryResult && (
        <div className="mb-6 rounded-lg border border-slate-300 bg-slate-50 p-4" data-testid="dry-run-result">
          <div className="text-[11px] uppercase text-slate-500 mb-2 font-semibold">
            Dry-run audit — nothing was changed
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><div className="text-slate-500 text-xs">Would assign (rules)</div><div className="font-bold text-slate-900">{dryResult.rule_applied ?? 0}</div></div>
            <div><div className="text-slate-500 text-xs">AI would apply</div><div className="font-bold text-slate-900">{dryResult.ai_applied ?? 0}</div></div>
            <div><div className="text-slate-500 text-xs">Would queue for review</div><div className="font-bold text-slate-900">{dryResult.queued ?? 0}</div></div>
            <div><div className="text-slate-500 text-xs">Would stay unassigned</div><div className="font-bold text-amber-700">{dryResult.unassigned_after ?? 0}</div></div>
          </div>
        </div>
      )}

      {/* Rollback panel */}
      <div className="mb-6 rounded-lg border border-rose-200 bg-rose-50/50 p-4" data-testid="rollback-panel">
        <div className="flex items-center gap-2 mb-2">
          <RefreshCcw className="w-4 h-4 text-rose-600" />
          <h2 className="text-sm font-semibold text-rose-900">Roll back a backfill run</h2>
        </div>
        <p className="text-xs text-slate-600 mb-3">
          Revert auto-applied market assignments made on/after a chosen time. Each
          affected talent's <b>previous</b> primary market is restored. Manually-chosen
          markets are preserved. Always preview first.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[11px] uppercase font-semibold text-slate-500 mb-1">Cutoff (assignments at/after)</label>
            <input
              type="datetime-local"
              value={rollbackSince}
              onChange={(e) => { setRollbackSince(e.target.value); setRollbackPreview(null); }}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              data-testid="rollback-since-input"
            />
          </div>
          <button
            type="button"
            onClick={previewRollback}
            disabled={busy || !rollbackSince}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-60"
            data-testid="rollback-preview-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />}
            Preview rollback
          </button>
          {rollbackPreview && (
            <button
              type="button"
              onClick={applyRollback}
              disabled={busy || !rollbackPreview.would_rollback}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-60"
              data-testid="rollback-apply-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              Roll back {rollbackPreview.would_rollback} assignment(s)
            </button>
          )}
        </div>
        {rollbackPreview && (
          <div className="mt-2 text-sm text-rose-800" data-testid="rollback-preview-result">
            <b>{rollbackPreview.would_rollback}</b> assignment(s) would be rolled back.
            {rollbackPreview.would_rollback === 0 && ' Nothing to do for this cutoff.'}
          </div>
        )}
      </div>

      {/* Geographic coverage by country */}
      {geo?.countries?.length > 0 && (
        <div className="mb-6" data-testid="geo-overview">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-cyan-600" />
            <h2 className="text-lg font-semibold text-slate-900">Geographic Coverage by Country</h2>
          </div>
          <div className="space-y-2">
            {geo.countries.map((c) => {
              const isOpen = openCountry === c.country;
              const isIntl = c.country !== 'US';
              return (
                <div key={c.country} className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`country-card-${c.country}`}>
                  <button
                    type="button"
                    onClick={() => setOpenCountry(isOpen ? null : c.country)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
                    data-testid={`country-toggle-${c.country}`}
                  >
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    <span className="font-semibold text-slate-900">{c.label}</span>
                    <span className="text-xs text-slate-400">{c.region_count} regions · {c.market_count} markets</span>
                    <span className="ml-auto flex items-center gap-3 text-sm">
                      <span className="font-bold text-slate-900" data-testid={`country-total-${c.country}`}>{c.total}</span>
                      <span className="text-emerald-700 text-xs">{c.approved} approved</span>
                      <span className="text-cyan-700 text-xs flex items-center gap-1"><Plane className="w-3 h-3" />{c.travel}</span>
                    </span>
                  </button>
                  {isOpen && (
                    <div className="border-t border-slate-100 px-4 py-3 space-y-3 bg-slate-50/50">
                      {c.regions.map((reg) => (
                        <div key={reg.region}>
                          <div className="text-[11px] uppercase font-semibold text-slate-500 mb-1">
                            {reg.region} <span className="text-slate-400 normal-case">· {reg.total} talent</span>
                          </div>
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                            {reg.markets.map((mk) => (
                              <div key={mk.metro_name} className="flex items-center justify-between rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs" data-testid={`market-row-${mk.metro_name}`}>
                                <span className="text-slate-700 truncate">{mk.metro_name}</span>
                                <span className="flex items-center gap-2 shrink-0 ml-2">
                                  <span className="font-semibold text-slate-900">{mk.total}</span>
                                  {isIntl && <span className="text-[10px] text-slate-400 uppercase">{mk.distance_unit}</span>}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Ambiguous international addresses */}
          {geo.ambiguous_count > 0 && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid="ambiguous-panel">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-semibold text-amber-900">
                  International addresses needing review ({geo.ambiguous_count})
                </h3>
              </div>
              <p className="text-xs text-slate-600 mb-3">
                Unassigned talent whose address looks international. Re-run assignment to place them,
                or review the address. This never affects bookings.
              </p>
              <div className="space-y-1.5">
                {geo.ambiguous.map((a) => (
                  <div key={a.talent_id} className="flex items-center gap-3 rounded border border-amber-200 bg-white px-3 py-2 text-sm" data-testid={`ambiguous-row-${a.talent_id}`}>
                    <span className="font-medium text-slate-800">{a.name}</span>
                    <span className="text-xs text-slate-500">{a.where}</span>
                    <span className="text-[10px] uppercase font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">{a.detected_country}</span>
                    <button
                      type="button"
                      onClick={() => reassignTalent(a.talent_id)}
                      disabled={busy}
                      className="ml-auto inline-flex items-center gap-1 rounded bg-cyan-600 hover:bg-cyan-700 text-white text-xs px-2.5 py-1.5 disabled:opacity-60"
                      data-testid={`ambiguous-reassign-${a.talent_id}`}
                    >
                      <RefreshCcw className="w-3.5 h-3.5" /> Re-run
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Queue */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList className="w-4 h-4 text-slate-500" />
          <h2 className="text-lg font-semibold text-slate-900">Review Queue</h2>
          <span className="text-xs text-slate-500">({queue.length})</span>
        </div>
        {queue.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500" data-testid="queue-empty">
            No AI suggestions awaiting review.
          </div>
        ) : (
          <div className="space-y-2">
            {queue.map((q) => (
              <div key={q.id} className="rounded-lg border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-3" data-testid={`queue-row-${q.id}`}>
                <div className="flex-1 min-w-[240px]">
                  <div className="text-sm font-semibold text-slate-900">
                    {q.talent_name || '(no name)'}
                    <span className="text-slate-400 font-normal ml-2 text-xs">
                      {q.talent_city || '—'}, {q.talent_state || '—'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    AI suggests: <b className="text-cyan-700">{q.suggested_market_name || '—'}</b>
                    <span className="ml-2 text-slate-400">confidence {q.confidence != null ? (q.confidence * 100).toFixed(0) + '%' : '?'}</span>
                  </div>
                  {q.reason && <div className="text-xs text-slate-500 italic mt-0.5">{q.reason}</div>}
                </div>
                <button
                  type="button"
                  onClick={() => approveQueue(q.id)}
                  disabled={loadingQueue}
                  className="inline-flex items-center gap-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2.5 py-1.5"
                  data-testid={`queue-approve-${q.id}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                </button>
                <button
                  type="button"
                  onClick={() => rejectQueue(q.id)}
                  disabled={loadingQueue}
                  className="inline-flex items-center gap-1 rounded bg-rose-100 hover:bg-rose-200 text-rose-800 text-xs px-2.5 py-1.5"
                  data-testid={`queue-reject-${q.id}`}
                >
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, tone = 'slate' }) {
  const tones = {
    slate:   'border-slate-200 bg-white text-slate-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    amber:   'border-amber-200 bg-amber-50 text-amber-900',
    rose:    'border-rose-200 bg-rose-50 text-rose-900',
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`}>
      <div className="text-[11px] uppercase font-semibold opacity-75">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
